"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import type {
  CFilesToDownloadContent,
  CFilesToDownloadItem,
} from "@/lib/api/cms-catalog";
import { fetchFilesList } from "@/lib/api/c-files-to-download";
import { useT } from "@/lib/i18n/context";
import { CatalogLoadMore } from "@/components/cms/Pagination/CatalogLoadMore";
import { cFilesToDownloadTranslations, type CFilesToDownloadTranslations } from "./CFilesToDownload.i18n";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./CFilesToDownload.module.css";

/**
 * CFilesToDownload - visitor view komponentu `c_files_to_download` (Pliki do pobrania).
 *
 * **Data source:** `C_FILES_TO_DOWNLOAD_CONTROLLER::getApiContent()` zwraca jeden
 * widok (`view: 'list'`) ze stanem poczatkowym (strona 1, wszystkie kategorie)
 * renderowanym SSR. Zmiana kategorii i doczytywanie kolejnych stron ("Wczytaj
 * wiecej") ida client-side przez `IAPI_listFiles` (proxy
 * `/api/c-files-to-download/list`) - filtr kategorii jest interakcja kliencka
 * (nie deep-linkowalna w URL, podobnie jak filtry client-side w innych katalogach).
 */
export function CFilesToDownload({ content }: CFilesToDownloadProps) {
  const t = useT(cFilesToDownloadTranslations);

  if (!content || typeof content !== "object") {
    return <div className={styles.empty}>{t.emptyData}</div>;
  }

  return <FilesList content={content} t={t} />;
}

export type CFilesToDownloadProps = {
  content: CFilesToDownloadContent;
};

function FilesList({
  content,
  t,
}: {
  content: CFilesToDownloadContent;
  t: CFilesToDownloadTranslations;
}) {
  const [category, setCategory] = useState<number>(content.selected_category);
  const [loading, setLoading] = useState(false);

  /* Stan strony 1 dla aktywnej kategorii (initialItems dla CatalogLoadMore).
     SSR podaje stronę 1 wszystkich kategorii; zmiana kategorii fetch'uje stronę 1
     wybranej kategorii (client-side przez `IAPI_listFiles`). */
  const [view, setView] = useState<FilesView>({
    items: content.items,
    totalPages: content.pagination.total_pages,
    totalCount: content.pagination.total_count,
  });

  /* Reset gdy SSR poda nowe dane (nawigacja / back-button). */
  const initRef = useRef(content);
  useEffect(() => {
    if (initRef.current !== content) {
      initRef.current = content;
      setCategory(content.selected_category);
      setView({
        items: content.items,
        totalPages: content.pagination.total_pages,
        totalCount: content.pagination.total_count,
      });
    }
  }, [content]);

  /* Zmiana kategorii - reset do strony 1 wybranej kategorii. Nowa referencja
     `view.items` remountuje paginację CatalogLoadMore (reset do strony 1). */
  const handleCategoryChange = useCallback(
    async (nextCategory: number) => {
      setCategory(nextCategory);
      setLoading(true);
      try {
        const res = await fetchFilesList(content.id, 1, nextCategory);
        if (res) {
          setView({
            items: res.items,
            totalPages: res.pagination.total_pages,
            totalCount: res.pagination.total_count,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [content.id],
  );

  /* Tryb kliencki CatalogLoadMore (`loadPage`) - filtr kategorii nie jest w URL,
     więc paginacja idzie przez nasz endpoint listy (z bieżącą kategorią), a nie
     przez współdzielony proxy treści katalogu. `updateUrl={false}` bo URL strony
     nie odzwierciedlałby przefiltrowanego zbioru. */
  const loadPage = useCallback(
    async (page: number): Promise<CFilesToDownloadItem[]> => {
      const res = await fetchFilesList(content.id, page, category);
      return res?.items ?? [];
    },
    [content.id, category],
  );

  return (
    <div className={styles.wrap}>
      {content.description && (
        <div
          className={styles.description}
          dangerouslySetInnerHTML={{ __html: content.description }}
        />
      )}

      <div className={styles.topBar}>
        {content.categories.length > 0 && (
          <label className={styles.filter}>
            <span className={styles.srOnly}>{t.categoryLabel}</span>
            <select
              className={styles.select}
              value={category}
              onChange={(e) => handleCategoryChange(Number(e.target.value))}
              disabled={loading}
            >
              <option value={0}>{t.allCategories}</option>
              {content.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {content.back_href && (
          <BackLink href={content.back_href} label={t.back} />
        )}
      </div>

      {view.items.length === 0 ? (
        <div className={styles.empty}>{t.emptyList}</div>
      ) : (
        <CatalogLoadMore<CFilesToDownloadItem>
          initialItems={view.items}
          initialPage={1}
          totalPages={view.totalPages}
          totalCount={view.totalCount}
          getKey={(item) => item.id}
          loadPage={loadPage}
          updateUrl={false}
        >
          {(items) => (
            <ul className={styles.list}>
              {items.map((item) => (
                <FileRow key={item.id} item={item} t={t} />
              ))}
            </ul>
          )}
        </CatalogLoadMore>
      )}
    </div>
  );
}

type FilesView = {
  items: CFilesToDownloadItem[];
  totalPages: number;
  totalCount: number;
};

/* ─── Pojedynczy wiersz pliku ────────────────────────────────────────────── */

function FileRow({
  item,
  t,
}: {
  item: CFilesToDownloadItem;
  t: CFilesToDownloadTranslations;
}) {
  const ext = item.ext ? item.ext.toUpperCase() : t.fileFallbackExt;
  const downloadHref = item.download_href;

  const inner = (
    <>
      <span className={styles.fileIcon} data-ext={item.ext || undefined} aria-hidden="true">
        {ext}
      </span>
      <span className={styles.fileBody}>
        <span className={styles.fileName}>{item.name}</span>
        <span className={styles.fileMeta}>
          {item.category_name && (
            <span className={styles.badge}>{item.category_name}</span>
          )}
          {item.size && Number(item.size) > 0 && (
            <span className={styles.fileSize}>{formatFileSize(item.size, t)}</span>
          )}
          {item.requires_login && (
            <span className={styles.badgeWarn}>{t.requiresLogin}</span>
          )}
          {item.requires_admin && (
            <span className={styles.badgeWarn}>{t.requiresAdmin}</span>
          )}
        </span>
      </span>
    </>
  );

  if (!downloadHref) {
    return <li className={styles.row}>{inner}</li>;
  }

  return (
    <li className={styles.row}>
      <a
        className={styles.fileLink}
        href={downloadHref}
        rel="noopener noreferrer"
        title={t.download}
      >
        {inner}
      </a>
    </li>
  );
}

/* ─── Back link (wewnetrzny <Link> lub natywny <a>) ──────────────────────── */

function BackLink({ href, label }: { href: string; label: string }) {
  const internal = extractInternalHref(href);
  if (internal !== null) {
    return (
      <Link href={internal} className={styles.backButton}>
        {label}
      </Link>
    );
  }
  return (
    <a href={href} className={styles.backButton}>
      {label}
    </a>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

/** Formatuje rozmiar (string MB z backendu) na czytelny string (KB/MB). */
function formatFileSize(mbStr: string, t: CFilesToDownloadTranslations): string {
  const mb = Number(mbStr);
  if (!Number.isFinite(mb) || mb <= 0) return "";
  if (mb < 1) {
    const kb = Math.round(mb * 1024);
    return `${kb} ${t.units.kb}`;
  }
  return `${mb.toFixed(2)} ${t.units.mb}`;
}

